import { useCallback, useEffect, useRef, useState } from "react"
import { useRouter } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"

import {
  ContinueJourneySection,
  downloadReportPdf,
  formatFileSize,
  GuidanceSection,
  KeepYourReportSection,
  ReportViewNavbar,
} from "@/features/report-view"
import { ReportIds, useUserReports } from "@/features/today"
import { useWebApp } from "@/features/webapp/hooks/use-webapp"

import { generateAuraScanPdf } from "../lib/generate-aura-scan-pdf"
import { isAuraScanReport } from "../types"
import { AuraScanReportView } from "./aura-scan-report-view"

/**
 * Aura Scan tab (`/webapp/aura-scan`): native report, PDF chrome, guidance.
 */
export function AuraScanPage() {
  const router = useRouter()
  const { setHeaderHidden } = useWebApp()
  const { byType, loading, refetch } = useUserReports()
  const reportRootRef = useRef<HTMLDivElement>(null)
  const blobRef = useRef<Blob | null>(null)
  const [sizeLabel, setSizeLabel] = useState("")
  const [pdfLoading, setPdfLoading] = useState(false)

  const report = byType(ReportIds.AuraScan)
  const status = report?.status
  const content =
    report?.content && isAuraScanReport(report.content) ? report.content : null

  useEffect(() => {
    setHeaderHidden(true)
    return () => setHeaderHidden(false)
  }, [setHeaderHidden])

  useEffect(() => {
    if (status !== "processing") return
    const timer = setInterval(() => void refetch(), 5000)
    return () => clearInterval(timer)
  }, [status, refetch])

  const ensurePdf = useCallback(async () => {
    if (report?.url) return report.url
    if (blobRef.current) return URL.createObjectURL(blobRef.current)
    const root =
      reportRootRef.current?.querySelector<HTMLElement>("[data-slot=aura-scan-report]") ??
      reportRootRef.current
    if (!root) throw new Error("Report is not ready")
    setPdfLoading(true)
    try {
      const blob = await generateAuraScanPdf(root)
      blobRef.current = blob
      setSizeLabel(formatFileSize(blob.size))
      return URL.createObjectURL(blob)
    } finally {
      setPdfLoading(false)
    }
  }, [report?.url])

  const handleDownload = async () => {
    try {
      const url = await ensurePdf()
      await downloadReportPdf(url, "aura-scan-report.pdf", blobRef.current)
    } catch {
      /* ignore: user can retry */
    }
  }

  if (status === "ready" && content) {
    return (
      <div className="-mx-4 bg-dark-base">
        <ReportViewNavbar
          sizeLabel={sizeLabel}
          loading={pdfLoading}
          onBack={() => router.history.back()}
          onDownload={() => void handleDownload()}
        />
        <div ref={reportRootRef}>
          <AuraScanReportView content={content} />
        </div>
        <div className="flex flex-col gap-10 px-4 py-10">
          <KeepYourReportSection
            onDownload={() => void handleDownload()}
            sizeLabel={sizeLabel}
            loading={pdfLoading}
          />
          <div className="h-px w-full bg-purple-light/10" />
          <GuidanceSection />
          <ContinueJourneySection currentId={ReportIds.AuraScan} />
        </div>
      </div>
    )
  }

  return (
    <div className="-mx-4">
      <AuraScanPlaceholder
        onBack={() => router.history.back()}
        spinning
        title="Composing your Aura Scan"
        subtitle={
          loading && !report
            ? "Reading your energy…"
            : "Your reading is being prepared. This can take a moment — check back shortly."
        }
      />
    </div>
  )
}

function AuraScanPlaceholder({
  onBack,
  title,
  subtitle,
  spinning = false,
}: {
  onBack: () => void
  title: string
  subtitle: string
  spinning?: boolean
}) {
  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-dark-base px-8 text-center">
      <div className="absolute inset-x-0 top-0 flex h-14 items-center px-4">
        <button
          type="button"
          aria-label="Back"
          onClick={onBack}
          className="flex size-8 items-center justify-center text-white [&_svg]:size-6"
        >
          <ArrowLeft />
        </button>
      </div>
      {spinning && (
        <span className="size-12 animate-spin rounded-full border-[3px] border-purple-light/20 border-t-purple-light" />
      )}
      <h1 className="mt-6 font-display text-2xl font-medium text-purple-light">{title}</h1>
      <p className="mt-2 max-w-[300px] font-body text-base text-purple-light/70">{subtitle}</p>
    </div>
  )
}
